import { moduleConfigSchema, type NormalizedModuleConfig } from "./types";
import { ACTIVE_STATES, MAX_VISIBLE_MODULES } from "./constants";

/**
 * Normalizes raw module config JSON into a fully typed shape.
 * Fills missing position (alphabetical default) and visible
 * (first N active modules) fields for legacy entries.
 * Returns empty object for malformed or missing input.
 */
export function normalizeModuleConfig(raw: unknown): NormalizedModuleConfig {
  const parsed = moduleConfigSchema.safeParse(raw);
  const modules = parsed.success ? parsed.data : {};

  const sortedKeys = Object.keys(modules).sort((a, b) => a.localeCompare(b));

  const result: NormalizedModuleConfig = {};
  let activeCount = 0;

  for (const key of sortedKeys) {
    const entry = modules[key];
    const position =
      typeof entry.position === "number"
        ? entry.position
        : sortedKeys.indexOf(key);
    const isActive = ACTIVE_STATES.has(entry.state);
    const visible =
      entry.visible ?? (isActive && activeCount < MAX_VISIBLE_MODULES);

    result[key] = {
      state: entry.state,
      position,
      visible,
      hidden: entry.hidden ?? false,
    };

    if (isActive) activeCount++;
  }

  return reindexPositions(result);
}

/** Re-indexes positions to sequential zero-based integers with no gaps */
function reindexPositions(
  config: NormalizedModuleConfig,
): NormalizedModuleConfig {
  const entries = Object.entries(config).sort(
    ([, a], [, b]) => a.position - b.position,
  );

  for (let i = 0; i < entries.length; i++) {
    entries[i][1].position = i;
  }

  return config;
}
